import { motion } from "framer-motion";
import { EASE } from "@/animations/motion";

interface IndustryDashboardProps {
  name: string;
  accent: string;
  kpis: { label: string; value: string }[];
}

/** Branded, CSS/SVG dashboard mockup — accent + KPIs give each industry its own identity. */
export function IndustryDashboard({ name, accent, kpis }: IndustryDashboardProps) {
  const bars = [46, 62, 40, 74, 58, 84, 70, 92];
  return (
    <div className="overflow-hidden rounded-[1.25rem] border border-border bg-card shadow-[0_30px_80px_-40px_rgba(15,23,42,0.35)]">
      <div
        className="flex items-center gap-2 px-5 py-3.5 text-white"
        style={{ background: accent }}
      >
        <span className="h-2.5 w-2.5 rounded-full bg-white/40" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/40" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/40" />
        <span className="ml-3 text-xs font-medium text-white/90">
          {name} · Operations Dashboard
        </span>
      </div>
      <div className="grid gap-4 p-5 sm:grid-cols-3">
        {kpis.slice(0, 3).map((k, i) => (
          <motion.div
            key={k.label}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: EASE, delay: 0.1 + i * 0.08 }}
            className="rounded-xl border border-border bg-background p-4"
          >
            <p className="text-xs text-muted-foreground">{k.label}</p>
            <p className="mt-1 font-heading text-xl font-bold text-foreground">{k.value}</p>
            <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full" style={{ width: `${60 + i * 12}%`, background: accent }} />
            </div>
          </motion.div>
        ))}
      </div>
      <div className="px-5 pb-6">
        <div className="rounded-xl border border-border bg-background p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Activity this week</p>
            <span
              className="rounded-full px-2.5 py-1 text-xs font-medium"
              style={{ background: `${accent}1a`, color: accent }}
            >
              Live
            </span>
          </div>
          <div className="mt-5 flex h-28 items-end gap-2">
            {bars.map((h, i) => (
              <motion.div
                key={i}
                initial={{ height: 0 }}
                whileInView={{ height: `${h}%` }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: EASE, delay: 0.15 + i * 0.05 }}
                className="flex-1 rounded-t-md"
                style={{ minHeight: 6, background: `linear-gradient(to top, ${accent}55, ${accent})` }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
